import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { membershipReports } from "../../api";
import { horseListService } from "../../api/horseList.service";
import { UserContext } from "../../contexts/UserContext";
import pedigreeIcon from "../../assets/icons/product-pedigree.svg";
import catalogueIcon from "../../assets/icons/product-catalogue.svg";
import multiReportIcon from "../../assets/icons/product-multi-report.svg";
import DashboardTestimonial from "./DashboardTestimonial";

const emptyDashboardData = {
	reports: [],
	reportCount: 0,
	lists: [],
	listCount: 0,
};

function getCollection(response) {
	if (Array.isArray(response)) return response;
	return Array.isArray(response?.results) ? response.results : [];
}

function getCount(response, collection) {
	return typeof response?.count === "number" ? response.count : collection.length;
}

function formatDashboardDate(value) {
	if (!value) return "Date unavailable";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "Date unavailable";
	return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(date);
}

function getReportStatus(report) {
	const status = report?.report?.status;
	if (status === "completed" && report.report?.id) return { label: "Ready", modifier: "ready" };
	if (status === "error") return { label: "Needs attention", modifier: "error" };
	if (status === "requested") return { label: "Queued", modifier: "pending" };
	return { label: "Generating", modifier: "pending" };
}

function DashboardSkeleton() {
	return (
		<div className="dashboard-skeleton" aria-label="Loading dashboard">
			<div className="dashboard-skeleton__line dashboard-skeleton__line--title" />
			<div className="dashboard-skeleton__cards">
				{[1, 2, 3].map((item) => <div className="dashboard-skeleton__card" key={item} />)}
			</div>
			<div className="dashboard-skeleton__panel" />
		</div>
	);
}

function HomeContent() {
	const user = useContext(UserContext);
	const [dashboardData, setDashboardData] = useState(emptyDashboardData);
	const [loading, setLoading] = useState(true);
	const [failedSections, setFailedSections] = useState([]);

	const loadDashboard = useCallback(async () => {
		setLoading(true);
		setFailedSections([]);

		const [reportsResult, listsResult] = await Promise.allSettled([
			membershipReports.details(),
			horseListService.list("?limit=4"),
		]);
		const failures = [];
		let reports = [];
		let reportCount = 0;
		let lists = [];
		let listCount = 0;

		if (reportsResult.status === "fulfilled") {
			reports = getCollection(reportsResult.value)
				.sort((first, second) => new Date(second.created) - new Date(first.created))
				.slice(0, 4);
			reportCount = getCount(reportsResult.value, reports);
		} else {
			failures.push("reports");
		}

		if (listsResult.status === "fulfilled") {
			lists = getCollection(listsResult.value).slice(0, 4);
			listCount = getCount(listsResult.value, lists);
		} else {
			failures.push("lists");
		}

		setDashboardData({ reports, reportCount, lists, listCount });
		setFailedSections(failures);
		setLoading(false);
	}, []);

	useEffect(() => {
		loadDashboard();
	}, [loadDashboard]);

	if (loading) return <DashboardSkeleton />;

	const firstName = user?.first_name || "there";
	const hasReports = dashboardData.reports.length > 0;
	const hasLists = dashboardData.lists.length > 0;
	const allSectionsFailed = failedSections.length === 2;

	return (
		<main className="dashboard">
			<section className="dashboard__welcome" aria-labelledby="dashboard-title">
				<div>
					<p className="dashboard__eyebrow">Your workspace</p>
					<h1 id="dashboard-title">Welcome back, {firstName}</h1>
					<p>Review your recent work or start a new pedigree analysis.</p>
				</div>
				<Link to="/new-pedigree" className="dashboard__primary-action" data-tour-id="dashboard-create-pedigree">
					<i className="fa fa-plus" aria-hidden="true" /> Create pedigree
				</Link>
			</section>

			{allSectionsFailed ? (
				<div className="dashboard-notice dashboard-notice--error" role="alert">
					<div>
						<strong>We could not load your workspace</strong>
						<span>Check your connection and try again.</span>
					</div>
					<button type="button" onClick={loadDashboard}>Try again</button>
				</div>
			) : failedSections.length ? (
				<div className="dashboard-notice" role="status">
					<span>Some workspace information could not be loaded.</span>
					<button type="button" onClick={loadDashboard}>Refresh</button>
				</div>
			) : null}

			<section className="dashboard-stats" aria-label="Account overview">
				<Link to="/membership" className="dashboard-stat">
					<span className="dashboard-stat__icon"><i className="fa fa-coins" aria-hidden="true" /></span>
					<span>
						<strong>{user?.credits ?? 0}</strong>
						<span>Available credits</span>
					</span>
					<i className="fa fa-chevron-right dashboard-stat__arrow" aria-hidden="true" />
				</Link>
				<Link to="/pedigree-analysis" className="dashboard-stat">
					<span className="dashboard-stat__icon"><img src={pedigreeIcon} alt="" aria-hidden="true" /></span>
					<span>
						<strong>{failedSections.includes("reports") ? "—" : dashboardData.reportCount}</strong>
						<span>Pedigree reports</span>
					</span>
					<i className="fa fa-chevron-right dashboard-stat__arrow" aria-hidden="true" />
				</Link>
				<Link to="/my-lists" className="dashboard-stat">
					<span className="dashboard-stat__icon"><i className="fa fa-list" aria-hidden="true" /></span>
					<span>
						<strong>{failedSections.includes("lists") ? "—" : dashboardData.listCount}</strong>
						<span>Horse lists</span>
					</span>
					<i className="fa fa-chevron-right dashboard-stat__arrow" aria-hidden="true" />
				</Link>
			</section>

			<div className="dashboard__grid">
				<div className="dashboard__main-column">
				<section className="dashboard-panel dashboard-panel--reports" aria-labelledby="recent-reports-title">
					<div className="dashboard-panel__header">
						<div>
							<h2 id="recent-reports-title">Recent pedigree reports</h2>
							<p>Track report progress and open completed results.</p>
						</div>
						<Link to="/pedigree-analysis">View all <span aria-hidden="true">→</span></Link>
					</div>
					{failedSections.includes("reports") ? (
						<div className="dashboard-section-error" role="status">
							<i className="fa fa-exclamation-circle" aria-hidden="true" />
							<span><strong>Reports are unavailable</strong><small>Refresh the dashboard to try again.</small></span>
						</div>
					) : hasReports ? (
						<ul className="dashboard-report-list">
							{dashboardData.reports.map((report) => {
								const status = getReportStatus(report);
								return (
									<li key={report.id}>
										<span className="dashboard-report-list__icon"><img src={pedigreeIcon} alt="" aria-hidden="true" /></span>
										<span className="dashboard-report-list__details">
											<strong>{report.name || "Untitled pedigree"}</strong>
											<span>{formatDashboardDate(report.created)}</span>
										</span>
										<span className={`dashboard-status dashboard-status--${status.modifier}`}>{status.label}</span>
										{report.report?.id && report.report?.status === "completed" ? (
											<Link to={`/reports/${report.report.id}`} className="dashboard-report-list__view" aria-label={`View ${report.name || "pedigree"} report`}>
												<i className="fa fa-eye" aria-hidden="true" />
											</Link>
										) : null}
									</li>
								);
							})}
						</ul>
					) : (
						<div className="dashboard-empty">
							<span className="dashboard-empty__icon"><img src={pedigreeIcon} alt="" aria-hidden="true" /></span>
							<h3>No pedigree reports yet</h3>
							<p>Create your first report to see its progress and results here.</p>
							<Link to="/new-pedigree">Create your first pedigree</Link>
						</div>
					)}
				</section>
				<DashboardTestimonial />
				</div>

				<div className="dashboard__side-column">
					<section className="dashboard-panel dashboard-panel--actions" aria-labelledby="quick-actions-title">
						<div className="dashboard-panel__header">
							<div><h2 id="quick-actions-title">Quick actions</h2><p>Jump straight into common tasks.</p></div>
						</div>
						<div className="dashboard-quick-actions">
							<Link to="/catalogues"><img src={catalogueIcon} alt="" aria-hidden="true" /><span><strong>Browse catalogues</strong><small>Explore analysed auction lots</small></span><i className="fa fa-chevron-right" aria-hidden="true" /></Link>
							<Link to="/new-list"><i className="fa fa-list" aria-hidden="true" /><span><strong>Create a horse list</strong><small>Organise sires and dams</small></span><i className="fa fa-chevron-right" aria-hidden="true" /></Link>
							<Link to="/premium"><img src={multiReportIcon} alt="" aria-hidden="true" /><span><strong>Run multiple reports</strong><small>Compare combinations in bulk</small></span><i className="fa fa-chevron-right" aria-hidden="true" /></Link>
						</div>
					</section>

					<section className="dashboard-panel dashboard-panel--lists" aria-labelledby="lists-title">
						<div className="dashboard-panel__header">
							<div><h2 id="lists-title">Horse lists</h2><p>Your most recent collections.</p></div>
							<Link to="/my-lists">View all <span aria-hidden="true">→</span></Link>
						</div>
						{failedSections.includes("lists") ? (
							<div className="dashboard-section-error dashboard-section-error--compact" role="status">
								<i className="fa fa-exclamation-circle" aria-hidden="true" />
								<span><strong>Lists are unavailable</strong><small>Refresh to try again.</small></span>
							</div>
						) : hasLists ? (
							<ul className="dashboard-list-preview">
								{dashboardData.lists.slice(0, 3).map((list) => {
									const details = <span><strong>{list.name || "Untitled list"}</strong><small>{list.type === "dam" ? "Dam list" : "Sire list"}{typeof list.total === "number" ? ` · ${list.total} horses` : ""}{list.preloaded ? " · Shared list" : ""}</small></span>;
									return (
										<li key={list.id}>
											{list.preloaded ? (
												<div className="dashboard-list-preview__shared">{details}</div>
											) : (
												<Link to={`/edit-list/${list.id}`}>{details}<i className="fa fa-chevron-right" aria-hidden="true" /></Link>
											)}
										</li>
									);
								})}
							</ul>
						) : (
							<div className="dashboard-empty dashboard-empty--compact">
								<p>No horse lists yet.</p>
								<Link to="/new-list">Create a list</Link>
							</div>
						)}
					</section>
				</div>
			</div>
		</main>
	);
}

export default HomeContent;
